/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Vault-passphrase recovery client. POSTs a `RecoveryBundle` to the backend's
 * decryption endpoint and returns the recovered plaintext passphrase.
 *
 * Pairs with `src/services/crypto/escrow.ts` (encryption side, used by
 * `TdPrintQr`). Backend contract: beyonddemise/app-backend#94.
 *
 * Caller: `src/components/TdForgotPass.ts` (recovery wizard).
 */

import { z } from 'zod';
import { tdFetch } from './tdfetch.js';
import type { RecoveryBundle } from './crypto/escrow.js';

const RECOVERY_ENDPOINT = '/api/auth/recover-passphrase';

const RecoverPassphraseResponseSchema = z.object({
  passphrase: z.string().min(1),
});

/**
 * Discriminator for `RecoveryServiceError` failure modes. Lets `TdForgotPass`
 * render different UI per cause without grepping message text.
 */
export type RecoveryFailureReason =
  | 'malformed-response' // server returned 200 with a body that fails `RecoverPassphraseResponseSchema`
  | 'authentication-failed' // 401 — bundle does not belong to this account or is corrupted
  | 'malformed-bundle' // 400 — server rejected the request body shape (caller-side bug)
  | 'rate-limited' // 429 — too many recovery attempts
  | 'unauthorized' // 403 — not authenticated as a member (no JWT or wrong role)
  | 'server-error' // 5xx — backend internal failure
  | 'network-error'; // fetch itself rejected (connection refused, offline, etc.)

export class RecoveryServiceError extends Error {
  readonly reason: RecoveryFailureReason;
  readonly httpStatus?: number;

  constructor(
    reason: RecoveryFailureReason,
    message: string,
    httpStatus?: number,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'RecoveryServiceError';
    this.reason = reason;
    if (httpStatus !== undefined) this.httpStatus = httpStatus;
  }
}

/**
 * Submit a `RecoveryBundle` to the backend for decryption.
 *
 * @param bundle - the parsed bundle (frontend parses the base64nopad-encoded
 *   QR payload into this shape via `RecoveryBundleSchema` before calling)
 * @returns the recovered plaintext passphrase
 * @throws {RecoveryServiceError} with a `reason` discriminator on every
 *   failure path
 */
export const recoverPassphrase = async (
  bundle: RecoveryBundle
): Promise<string> => {
  let response: Response;
  try {
    response = await tdFetch(RECOVERY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recoveryBundle: bundle }),
    });
  } catch (cause) {
    throw new RecoveryServiceError(
      'network-error',
      cause instanceof Error
        ? `recovery request failed: ${cause.message}`
        : 'recovery request failed',
      undefined,
      { cause }
    );
  }

  if (!response.ok) {
    const reason: RecoveryFailureReason =
      response.status === 401
        ? 'authentication-failed'
        : response.status === 403
          ? 'unauthorized'
          : response.status === 400
            ? 'malformed-bundle'
            : response.status === 429
              ? 'rate-limited'
              : response.status >= 500
                ? 'server-error'
                : 'authentication-failed';
    throw new RecoveryServiceError(
      reason,
      `recovery request rejected with HTTP ${response.status}`,
      response.status
    );
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (cause) {
    throw new RecoveryServiceError(
      'malformed-response',
      'recovery response was not valid JSON',
      response.status,
      { cause }
    );
  }

  const parsed = RecoverPassphraseResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new RecoveryServiceError(
      'malformed-response',
      `recovery response shape rejected: ${parsed.error.message}`,
      response.status,
      { cause: parsed.error }
    );
  }
  return parsed.data.passphrase;
};
